import * as i0 from '@angular/core';
import { PLATFORM_ID, Injectable, Inject, NgModule, Optional, SkipSelf } from '@angular/core';
import { BehaviorSubject, Subject, of, Observable, from, throwError } from 'rxjs';
import * as i1 from '@angular/router';
import { WebAuth } from 'auth0-js';
import { mergeMap, tap, take, switchMap, catchError } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

class LocalStorageService {
    constructor(platformId) {
        this.platformId = platformId;
    }
    get(key) {
        return isPlatformBrowser(this.platformId)
            ? localStorage?.getItem(key)
            : null;
    }
    set(key, data) {
        if (isPlatformBrowser(this.platformId)) {
            localStorage?.setItem(key, data);
        }
    }
    remove(key) {
        if (isPlatformBrowser(this.platformId)) {
            localStorage?.removeItem(key);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: LocalStorageService, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: LocalStorageService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: LocalStorageService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }]; } });

class TokensService {
    constructor(environment, platformId, lsService) {
        this.environment = environment;
        this.platformId = platformId;
        this.lsService = lsService;
        this.current$ = new BehaviorSubject(this.lsService.get(this.environment.auth0.domain));
        this.otherPageChange$ = new Subject();
        this.storageListener = () => {
            const token = this.lsService.get(this.environment.auth0.domain);
            if (token !== this.current$.value) {
                this.current$.next(token);
                this.otherPageChange$.next();
            }
        };
        if (isPlatformBrowser(this.platformId) && window.addEventListener) {
            window.addEventListener('storage', this.storageListener, false);
        }
    }
    ngOnDestroy() {
        if (isPlatformBrowser(this.platformId)) {
            window.removeEventListener('storage', this.storageListener, false);
        }
    }
    set(token) {
        if (token) {
            this.lsService.set(this.environment.auth0.domain, token);
        }
        else {
            this.lsService.remove(this.environment.auth0.domain);
        }
        this.current$.next(token);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: TokensService, deps: [{ token: 'environment' }, { token: PLATFORM_ID }, { token: LocalStorageService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: TokensService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: TokensService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: LocalStorageService }]; } });

class Auth0Service {
    constructor(environment, tokens) {
        this.environment = environment;
        this.tokens = tokens;
        this.refreshTokenSubject = new Subject();
        this.isRefreshing = false;
        this.auth0 = new WebAuth(this.environment.auth0);
    }
    get token$() {
        return this.tokens.current$.pipe(mergeMap((token) => {
            const { accessToken, refreshToken } = JSON.parse(token || '{}');
            if (accessToken && new JwtHelperService().isTokenExpired(accessToken) && refreshToken) {
                return this.refreshAccessToken();
            }
            return of(token);
        }));
    }
    get account$() {
        return this.token$.pipe(mergeMap((token) => {
            return new Observable((observer) => {
                if (token) {
                    this.auth0.client.userInfo(JSON.parse(token).accessToken, (_, user) => {
                        observer.next(user);
                    });
                }
                else {
                    observer.next(null);
                }
            });
        }));
    }
    loginWithUsernameAndPassword(username, password) {
        this.tokens.set(null);
        return new Observable((observer) => {
            this.auth0.client.login({
                username,
                password,
                realm: 'Username-Password-Authentication',
                scope: 'openid offline_access'
            }, (error, result) => {
                if (error) {
                    this.tokens.set(null);
                    observer.error(error);
                }
                else {
                    this.tokens.set(JSON.stringify(result));
                    observer.next(result);
                }
            });
        });
    }
    logout() {
        this.tokens.set(null);
    }
    refresh() {
        return new Observable((observer) => {
            const { refreshToken } = JSON.parse(this.tokens.current$.value || '{}');
            if (refreshToken) {
                const options = {
                    grantType: 'refresh_token',
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    refresh_token: refreshToken
                };
                this.auth0.client.oauthToken(options, (_, result) => {
                    if (result) {
                        result.refreshToken = refreshToken;
                    }
                    this.tokens.set(result ? JSON.stringify(result) : result);
                    observer.next(this.tokens.current$.value);
                });
            }
            else {
                observer.next(this.tokens.current$.value);
            }
        });
    }
    refreshAccessToken() {
        // If another API call has already started refresh listen to refreshTokenSubject and
        // Wait for that to complete.
        if (this.isRefreshing) {
            return this.refreshTokenSubject;
        }
        this.isRefreshing = true;
        return this.refresh()
            .pipe(tap(() => {
            this.isRefreshing = false;
            this.refreshTokenSubject.next(this.tokens.current$.value);
        }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Service, deps: [{ token: 'environment' }, { token: TokensService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Service, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Service, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: TokensService }]; } });

class Auth0Guard {
    constructor(environment, router, auth0) {
        this.environment = environment;
        this.router = router;
        this.auth0 = auth0;
        this.unauthorizedRedirectUrl = '/accounts/check';
        if (this.environment.unauthorizedRedirectUrl !== undefined) {
            this.unauthorizedRedirectUrl = this.environment.unauthorizedRedirectUrl;
        }
    }
    canActivate(route, state) {
        return this.canActivateChild(route, state);
    }
    canActivateChild(route, state) {
        const canActivate = !!this.auth0.tokens.current$.value;
        return canActivate
            ? of(canActivate)
            : from(this.router.navigate([this.unauthorizedRedirectUrl], {
                replaceUrl: true,
                queryParams: { returnUrl: state.url },
            }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Guard, deps: [{ token: 'environment' }, { token: i1.Router }, { token: Auth0Service }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Guard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Guard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: i1.Router }, { type: Auth0Service }]; } });

class AuthorizationInterceptor {
    constructor(environment, auth0) {
        this.environment = environment;
        this.auth0 = auth0;
    }
    intercept(request, next) {
        if (request?.url?.startsWith(this.environment.api) ||
            request?.url?.startsWith('/api') ||
            // This is to support shop APIS
            request?.url?.includes('/payment-gateway')) {
            return this.auth0.token$.pipe(take(1), switchMap((token) => {
                const { tokenType, accessToken } = JSON.parse(token || '{}');
                let headers = {};
                if (tokenType && accessToken) {
                    headers = {
                        ...headers,
                        // eslint-disable-next-line @typescript-eslint/naming-convention
                        Authorization: `${tokenType} ${accessToken}`,
                    };
                }
                request = request.clone({
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    setHeaders: { ...headers, 'Content-Type': 'application/json' },
                    url: this.prependUrl(request.url),
                });
                return next.handle(request);
            }), catchError((error) => {
                return throwError(error);
            }));
        }
        return next.handle(request);
    }
    /**
     * Prepend domain to all API calls that start with /api
     * @param url Current url
     */
    prependUrl(url) {
        if (url.startsWith('/api')) {
            return `${this.environment.apiUrl}${url}`;
        }
        return url;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: AuthorizationInterceptor, deps: [{ token: 'environment' }, { token: Auth0Service }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: AuthorizationInterceptor, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: AuthorizationInterceptor, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: Auth0Service }]; } });

class UnAuth0Guard {
    constructor(environment, router, auth0) {
        this.environment = environment;
        this.router = router;
        this.auth0 = auth0;
        this.authorizedRedirectUrl = '/';
        if (this.environment.authorizedRedirectUrl !== undefined) {
            this.authorizedRedirectUrl = this.environment.authorizedRedirectUrl;
        }
    }
    canActivate() {
        return this.canActivateChild();
    }
    canActivateChild() {
        return of(this.auth0.tokens.current$.value ? this.router.parseUrl(this.authorizedRedirectUrl) : true);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: UnAuth0Guard, deps: [{ token: 'environment' }, { token: i1.Router }, { token: Auth0Service }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: UnAuth0Guard, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: UnAuth0Guard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: ['environment']
                }] }, { type: i1.Router }, { type: Auth0Service }]; } });

class Auth0Module {
    constructor(parentModule) {
        if (parentModule) {
            throw new Error('Auth0Module is already loaded. Import it in the AppModule only');
        }
    }
    static forRoot(environment) {
        return {
            ngModule: Auth0Module,
            providers: [
                {
                    provide: 'environment',
                    useValue: environment
                },
                Auth0Service
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Module, deps: [{ token: Auth0Module, optional: true, skipSelf: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.2", ngImport: i0, type: Auth0Module, imports: [CommonModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Module, providers: [
            Auth0Service,
            TokensService,
            Auth0Guard,
            UnAuth0Guard,
            {
                provide: HTTP_INTERCEPTORS,
                useClass: AuthorizationInterceptor,
                multi: true
            }
        ], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.2", ngImport: i0, type: Auth0Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    providers: [
                        Auth0Service,
                        TokensService,
                        Auth0Guard,
                        UnAuth0Guard,
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: AuthorizationInterceptor,
                            multi: true
                        }
                    ]
                }]
        }], ctorParameters: function () { return [{ type: Auth0Module, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }]; } });

/*
 * Public API Surface of library
 */

/**
 * Generated bundle index. Do not edit.
 */

export { Auth0Guard, Auth0Module, Auth0Service, AuthorizationInterceptor, TokensService, UnAuth0Guard };
